import { f7, useStore } from 'framework7-react';
import { FC, useRef } from 'react';
import styles from './RegisterNewMemberStep4Style.module.css';
import { getCarCandidates } from '@/api/registerQRApi';
import RegisterInviteCodeImg from '@/assets/images/register-qr/register-scan.png';
import { Button } from '@/components/uiParts/Button';
import { isApp } from '@/config/device';
import { RegisterUserDataType } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { CarCandidate } from '@/types/api/registerQRApi';
import { storeDispatch } from '@/utils/store';
import { escapeHtml } from '@/utils/utils';

interface RegisterNewMemberStep4Props {
  hanldeCancel: () => void;
  showSkip?: boolean;
}

export const RegisterNewMemberStep4: FC<RegisterNewMemberStep4Props> = ({
  hanldeCancel,
  showSkip = true,
}) => {
  const registerUserData = useStore(
    store,
    'getRegisterUser',
  ) as RegisterUserDataType;

  // 再スキャンを続けて行った際に、古い候補取得の応答が後から返って
  // 新しいスキャン内容を巻き戻さないよう、最新リクエストのみを採用する
  const latestScanRequestId = useRef(0);

  const showCarSelectionDialog = (
    candidates: CarCandidate[],
    scanData: Record<string, any>,
  ) => {
    const radioButtons = candidates
      .map(
        (c, i) =>
          `<label class="radio" style="display:flex;align-items:center;margin:8px 0;"><input type="radio" name="car-candidate" value="${i}" ${
            i === 0 ? 'checked' : ''
          }/><i class="icon-radio"></i><span style="margin-left:8px;">${escapeHtml(
            c.mk_nm,
          )} ${escapeHtml(c.car_nm)}</span></label>`,
      )
      .join('');

    f7.dialog
      .create({
        title: '車両の選択',
        text: 'お乗りのクルマを選択してください',
        content: `<div style="padding:0 16px;">${radioButtons}</div>`,
        buttons: [
          {
            text: '決定',
            bold: true,
            onClick: (_dialog: any, e: any) => {
              const checked = document.querySelector(
                'input[name="car-candidate"]:checked',
              ) as HTMLInputElement | null;
              const idx = checked ? parseInt(checked.value, 10) : 0;
              const selected = candidates[idx];
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: {
                  ...scanData,
                  carnm_cd: selected.carnm_cd,
                  mk_cd: selected.mk_cd,
                  maker_name: selected.mk_nm,
                  car_name: selected.car_nm,
                },
              });
            },
          },
        ],
      })
      .open();
  };

  window.onInspectionCertificateScan = (data) => {
    try {
      const parsed: {
        transport_bu_name: string;
        class_no: string;
        hiragana: string;
        registry_no: string;
        registry_no_erased?: number;
        businesswork_flg?: number;
        inspection_certificate_file: string | File | Blob; // Base64 image if necessary
        vin: string;
        motor_model?: string;
        katasiki_num?: number;
        ruibetsu_num?: number;
        inspection_limit: string; // YYYY/MM/DD
        model_code: string;
        first_registration: string; // YYYY-MM-DD
      } = JSON.parse(data);

      const baseScanData = {
        ...parsed,
        next_inspection_date: parsed.inspection_limit,
        registration_date: parsed.first_registration,
      };

      // 候補取得を待たずに先に反映する。API待ちの間に離脱・登録されると
      // 前回スキャンの内容が残ってしまうため。
      storeDispatch('setRegisterUser', 'registerUser', {
        ...registerUserData,
        scan: baseScanData,
      });

      if (parsed.model_code) {
        const requestId = ++latestScanRequestId.current;
        getCarCandidates(parsed.model_code)
          .then((res) => {
            // 後続のスキャンが始まっていれば、この応答は破棄する
            // (遷移も後続の応答側で行う)
            if (requestId !== latestScanRequestId.current) return;
            const candidates = res?.data?.candidates ?? [];
            if (candidates.length === 1) {
              const c = candidates[0];
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: {
                  ...baseScanData,
                  carnm_cd: c.carnm_cd,
                  mk_cd: c.mk_cd,
                  maker_name: c.mk_nm,
                  car_name: c.car_nm,
                },
              });
            }
            // ダイアログは確認画面へ遷移してから開く
            f7.view.main.router.navigate(paths.registerNewMemberConfirm);
            if (candidates.length >= 2) {
              showCarSelectionDialog(candidates, baseScanData);
            }
          })
          .catch(() => {
            if (requestId !== latestScanRequestId.current) return;
            f7.view.main.router.navigate(paths.registerNewMemberConfirm);
          });
      } else {
        f7.view.main.router.navigate(paths.registerNewMemberConfirm);
      }
    } catch {}
  };

  const openInspectionCertificateScan = () => {
    if (isApp) {
      window.location.href = '/inspection_certificate_reader';
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['main-image']}>
        <img src={RegisterInviteCodeImg} />
      </div>
      <div className={styles['content']}>
        <div className={styles['heading']}>
          <h3>車検証の登録を行いましょう</h3>
        </div>
        <div className={styles['button-group']}>
          <Button
            className={styles['button']}
            onClick={openInspectionCertificateScan}
          >
            車検証を登録
          </Button>
          {showSkip && (
            <Button
              className={styles['button']}
              outline
              bgColor="white"
              onClick={hanldeCancel}
            >
              スキップ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
